// React i hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Navigacija
import { Routes } from "../../navigation";
import { Routes as FleetTireRoutes } from "ui/modules/fleet/tires/navigation";

// Helpers
import { transformRoute, useTranslate } from "helpers";

// API
import { getFleetTiresAPI } from "services/api/fleet/tires/api";
import { FleetTireRow } from "services/api/fleet/tires/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";

// Komponente
import { TireSearchPageCmp, TireSearchPageProps } from "./types";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Pagination } from "services/search";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import { NoResults } from "ui/components/NoResults";

// Stranice / Komponente pretrage
import TireSearchResults from "./components/TireSearchResults/TireSearchResults";
import TireSearchFilters from "./components/TireSearchFilters/TireSearchFilters";

// Konstante
import {
  FLEET_TIRE_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { SIDEBAR_ITEM_FLEET_TIRE_NEW } from "app/components/Sidebar/consts";

import TabSearchMenu from "../components/TabSearchMenu/TabSearchMenu";
import TireAddModal from "../TireAddModal/TireAddModal";

const TireSearchPage: TireSearchPageCmp = (props: TireSearchPageProps) => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetTireRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (pagination: Pagination) => {
    setIsLoading(true);
    try {
      const results = await getFleetTiresAPI(pagination);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(FleetTireRoutes.FleetTireDetailPageRouter, {
        id,
      })
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setShowAddNewModal(true);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setShowAddNewModal(false);
  }, []);

  const handleSearchChange = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <PageTitle
              title={FLEET_TIRE_SEARCH_PAGE_TITLE.text}
              icon={FLEET_TIRE_SEARCH_PAGE_TITLE.icon}
              iconColor={FLEET_TIRE_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
        </div>
        <TabSearchMenu />
        <div className="px-0 pt-6 mx-[-1px] xl:px-6">
          <TireSearchFilters
            onAddNew={handleAddNew}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message={t("admin.fleet.tire.set.search.noresults")} />
          ) : (
            <TireSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>

      <Modal
        show={showAddNewModal}
        onHide={() => setShowAddNewModal(false)}
        // size="small"
        position="centered"
      >
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title={SIDEBAR_ITEM_FLEET_TIRE_NEW.text}
          onHide={() => setShowAddNewModal(false)}
        />

        <ModalBody>
          <TireAddModal
            onSuccess={handleEditSuccess}
            onClose={() => setShowAddNewModal(false)}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

TireSearchPage.route = Routes.FleetTireSearchPageRouter;

export default TireSearchPage;
